import { useState, ReactNode, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/store";
import { supabase } from "@/supabaseClient";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/Combobox";
import { ClientInvoicesTable } from "@/components/tables/ClientInvoicesTable";

interface ClientDeleteButtonProps {
  client: {
    id: number;
    name: string;
  };
  children: ReactNode;
}

export function ClientDeleteButton({ client, children }: ClientDeleteButtonProps) {
  const navigate = useNavigate();
  const { clients } = useStore((s) => s);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<{
    id: string | number;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasInvoices, setHasInvoices] = useState(false);
  const [isClientComboboxOpen, setClientComboboxOpen] = useState<boolean>(false);

  // Filter out the current client from the dropdown options
  const otherClients = clients.filter((c) => c.id !== client.id);

  // Format clients for the Combobox
  const clientOptions = otherClients.map((otherClient) => ({
    id: otherClient.id,
    name: otherClient.name,
  }));

  // Check if the client has invoices when modal opens
  useEffect(() => {
    if (showDeleteModal) {
      const checkClientInvoices = async () => {
        try {
          const { data, error } = await supabase
            .from("invoices")
            .select("id")
            .eq("client_id", client.id);

          if (error) {
            throw error;
          }

          setHasInvoices(data && data.length > 0);
        } catch (error: unknown) {
          console.error("Error checking client invoices:", error);
        }
      };

      checkClientInvoices();
    }
  }, [client.id, showDeleteModal]);

  const handleClientSelect = (option: { id: string | number; name: string }) => {
    setSelectedClient(option);
  };

  const handleDelete = async () => {
    const store = useStore.getState();
    try {
      setIsDeleting(true);

      // If client has invoices and a target client is selected, transfer invoices
      if (hasInvoices && selectedClient) {
        // First, get all invoices for this client
        const { data: clientInvoices, error: fetchError } = await supabase
          .from("invoices")
          .select("*")
          .eq("client_id", client.id);

        if (fetchError) {
          throw fetchError;
        }

        // Update each invoice with the new client_id and contact_name
        if (clientInvoices && clientInvoices.length > 0) {
          const { error: updateError } = await supabase
            .from("invoices")
            .update({
              client_id: selectedClient.id,
              contact_name: selectedClient.name,
            })
            .eq("client_id", client.id);

          if (updateError) {
            throw updateError;
          }

          // Also update the invoices in the local store
          const { invoices } = store;
          const updatedInvoices = invoices.map((invoice) => {
            if (invoice.client_id === client.id) {
              return {
                ...invoice,
                client_id:
                  typeof selectedClient.id === "string"
                    ? parseInt(selectedClient.id, 10)
                    : selectedClient.id,
                contact_name: selectedClient.name,
              };
            }
            return invoice;
          });

          // Update invoices in the store
          store.setInvoices(updatedInvoices);

          // Also fetch the latest target client data to ensure it's up to date
          const { data: targetClientData } = await supabase
            .from("clients")
            .select("*")
            .eq("id", selectedClient.id)
            .single();

          if (targetClientData) {
            // Update the clients in the store with the latest target client data
            const updatedClients = store.clients.map((c) => {
              if (c.id === targetClientData.id) {
                return targetClientData;
              }
              return c;
            });
            store.setClients(updatedClients);
          }
        }
      }

      // Delete the client
      const { error: deleteError } = await supabase.from("clients").delete().eq("id", client.id);

      if (deleteError) {
        throw deleteError;
      }

      // Update the local state by removing the deleted client
      const updatedClients = clients.filter((c) => c.id !== client.id);
      store.setClients(updatedClients);

      toast.success("Klientas sėkmingai ištrintas");
      setShowDeleteModal(false);

      // If invoices were transferred, redirect to the target client page
      if (hasInvoices && selectedClient) {
        // First fetch the target client data to ensure we have the latest data
        const { data: targetClientData } = await supabase
          .from("clients")
          .select("*")
          .eq("id", selectedClient.id)
          .single();

        if (targetClientData) {
          // Update the client in the store to ensure it's up to date
          const updatedClients = clients.map((c) => {
            if (c.id === targetClientData.id) {
              return targetClientData;
            }
            return c;
          });
          store.setClients(updatedClients);
        }

        // Navigate to the target client page
        navigate(`/clients`);
      }
    } catch (error: unknown) {
      console.error("Error deleting client:", error);
      const errorMessage = error instanceof Error ? error.message : "Nežinoma klaida";
      toast.error(`Trinant klientą įvyko klaida: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div onClick={() => setShowDeleteModal(true)}>{children}</div>

      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Ištrinti klientą
            </DialogTitle>
            <DialogDescription>
              {hasInvoices
                ? "Šis klientas turi sąskaitų. Prieš ištrinant, pasirinkite kitą klientą, kuriam bus priskirtos šio kliento sąskaitos."
                : "Ar tikrai norite ištrinti šį klientą?"}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <p className="font-medium mb-2">Klientas: {client.name}</p>

            {hasInvoices && (
              <>
                <div className="mt-4 mb-6">
                  <label className="text-sm font-medium mb-2 block">
                    Pasirinkite klientą, kuriam perkelti sąskaitas:
                  </label>
                  <Combobox
                    options={clientOptions}
                    isOpen={isClientComboboxOpen}
                    onComboboxOpenChange={setClientComboboxOpen}
                    selectedOption={selectedClient}
                    onSelect={handleClientSelect}
                    selectLabel="Pasirinkite klientą"
                    searchLabel="Ieškoti kliento..."
                    notFoundLabel="Klientas nerastas."
                  />
                </div>

                <div className="mt-6 border-t pt-4">
                  <h3 className="text-sm font-medium mb-2">
                    Kliento sąskaitos, kurios bus perkeltos:
                  </h3>
                  <div className="max-h-60 overflow-y-auto">
                    <ClientInvoicesTable clientId={client.id} />
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Atšaukti
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || (hasInvoices && !selectedClient)}
            >
              {isDeleting ? "Trinama..." : "Ištrinti"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
